import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, FormControlLabel, Grid2 as Grid, IconButton, Stack, Switch, Typography, useMediaQuery } from "@mui/material";
import { Plus } from "@phosphor-icons/react";
import { GenericDialog } from "../../../components/generic/GenericDialog";
import { FormTextField } from "../../../components/generic/GenericTextFields";
import LoadingButton from "@mui/lab/LoadingButton";
import { createCategory, updateCategory } from "../../../config/apisauce";
import { useFormik } from "formik";
import { object, string } from "yup";
import { PLATFORMS_KEY, SOCIALS_KEY, TAGS_KEY } from "../../../enums/generalEnums";
import { manageAddOrUpdate } from "../../../helpers/arrayHelper";
import useNotiStack from "../../../hooks/useNotiStack";
import { useTheme } from "@emotion/react";
import { ACTIVE } from "../../../enums/statusEnums";

export const TagManagmentHeader = (props) => {
  const {
    title,
    subTitle,
    selectedCategory,
    alterList,
    openAddDialog,
    setOpenAddDialog,
    setSelectedCategory,
    type = TAGS_KEY
  } = props;

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const { success } = useNotiStack();
  const hasPlaceHolder = (type === SOCIALS_KEY || type === PLATFORMS_KEY);
  const [showFollowerCount, setShowFollowerCount] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  useEffect(() => {
    if (openAddDialog) {
      setShowFollowerCount(selectedCategory?.meta?.showFollowerCount ?? false);
      setIsFeatured(selectedCategory?.meta?.featured ?? false);
    };
  }, [openAddDialog, selectedCategory]);

  const formik = useFormik({
    initialValues: {
      name: selectedCategory?.name ?? '',
      ...(hasPlaceHolder && {
        placeholder: selectedCategory?.meta?.placeholder ?? ''
      })
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: object().shape({
      'name': string().trim().required(`${title} name is required`),
      ...(hasPlaceHolder && { 'placeholder': string().trim().required('Placeholder is required') })
    }),
    onSubmit: (values) => handleUpdateOrCreateCategory(values),
  });

  const handleCloseDialog = () => {
    setSelectedCategory(null);
    setOpenAddDialog(false);
  };

  const handleUpdateOrCreateCategory = async (values) => {
    const meta = {
      ...(hasPlaceHolder && {
        meta: {
          ...selectedCategory?.meta,
          ...(type === SOCIALS_KEY && {
            showFollowerCount: showFollowerCount,
            featured: isFeatured
          }),
          placeholder: values?.placeholder
        }
      })
    };
    const response = selectedCategory ? await updateCategory({
      id: selectedCategory?.id,
      name: values?.name,
      status: ACTIVE,
      ...meta
    }) : await createCategory({ name: values?.name, type: type, ...meta });
    if (response.ok) {
      alterList((prev) => {
        const currentList = { ...prev };
        currentList.data = manageAddOrUpdate(currentList.data, response?.data);
        return currentList;
      });
      handleCloseDialog();
      success('Successfully added!');
    }
  };

  return (
    <Fragment>
      <Grid container justifyContent={'space-between'} alignItems={'center'}>
        <Grid item>
          <Typography variant="h6" color="primary" fontWeight={700}>{title}</Typography>
        </Grid>
        <Grid item>
          {
            isXs ? <IconButton color="primary" onClick={() => setOpenAddDialog(true)}>
              <Plus size={24} weight="bold" />
            </IconButton> : <Button sx={{ width: 130 }} startIcon={<Plus size={16} weight="bold" />} onClick={() => setOpenAddDialog(true)}>Add More</Button>
          }
        </Grid>
      </Grid>
      <Typography fontWeight={500} mb={2}>{subTitle}</Typography>
      <GenericDialog
        title={selectedCategory ? 'Edit' : 'Add'}
        width={'645px'}
        openDialog={openAddDialog}
        handleClose={() => handleCloseDialog()}
      >
        <Typography mb={1}>To {selectedCategory ? 'edit' : 'create a new '} <b>{title}</b>, simply enter the desired {hasPlaceHolder ? 'details' : 'name'} in the input field below</Typography>
        <FormTextField
          value={formik?.values?.name}
          isRequired={false}
          placeholder={`${title} Name`}
          handleChange={(value) => formik.setFieldValue('name', value)}
          error={Boolean(formik?.errors?.name)}
          helperText={formik?.errors?.name}
        />
        {
          hasPlaceHolder && <FormTextField
            value={formik?.values?.placeholder}
            isRequired={false}
            placeholder={'Placeholder'}
            handleChange={(value) => formik.setFieldValue('placeholder', value)}
            error={Boolean(formik?.errors?.placeholder)}
            helperText={formik?.errors?.placeholder}
          />
        }
        {
          (type === SOCIALS_KEY) && <Box mt={2}>
            <FormControlLabel control={<Switch onChange={() => setShowFollowerCount((prev) => !prev)} checked={showFollowerCount} />} label="Show Follower Count Field" />
            <Typography mb={1.5}>Displays a field where the creator can input their follower count.</Typography>
            <FormControlLabel control={<Switch onChange={() => setIsFeatured((prev) => !prev)} checked={isFeatured} />} label="Featured" />
            <Typography>This social is displayed on the creator&apos;s card on the home page.</Typography>
          </Box>
        }
        <Box display={{ sm: 'flex' }} justifyContent={'flex-end'} mt={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <Button variant="outlined" sx={{ width: { xs: '100%', sm: '250px' } }} onClick={() => handleCloseDialog()}>Cancel</Button>
            <LoadingButton
              onClick={() => formik.handleSubmit()}
              variant="contained"
              sx={{ width: { xs: '100%', sm: '250px' } }}
            >
              {selectedCategory ? 'Save' : 'Add'}
            </LoadingButton>
          </Stack>
        </Box>
      </GenericDialog>
    </Fragment>
  );
};